<script lang="ts">
	/**
	 * Which account a purchase will land on (Design §14).
	 *
	 * Signing in became invisible the day it stopped being a redirect: a customer who confirms a
	 * purchase now goes from the product page to the payment page without ever seeing the identity
	 * provider's chrome, so nothing on the way tells them *whose* subscription they are about to
	 * create. On a shared or family machine that is a purchase attached to the wrong person, and
	 * it is discovered when the entitlement fails to appear — by which point the money has moved
	 * and the fix is a support conversation about a refund.
	 *
	 * So the two screens where the decision is actually made say it, in one sentence, from one
	 * component: the confirmation window on the product page, and `/Checkout/Start`.
	 *
	 * It renders **nothing at all** when signed out. There is no account for the purchase to be on
	 * yet, and a line saying so would be a second answer to a question `SignInPanel` is already
	 * asking a few pixels away.
	 *
	 * The email, not the name: it is what identifies the account to the person reading it, it is
	 * where the receipt will go, and an account AbstractID created from an address alone has no
	 * name to show anyway. The name is shown beside it only when there is one.
	 */

	import AbstractID, { isAuthorized } from '../AbstractID';
	import { displayNameOf } from '../Utilities';

	/** Lead-in wording. The confirmation window and the checkout page frame it differently. */
	export let label: string = 'Purchasing as';

	$: tokenData = $isAuthorized ? AbstractID.tokenData : null;
	$: email = tokenData?.email ?? '';
	/** Blank whenever the account has no name of its own — never a lone space. */
	$: name = displayNameOf(tokenData) === email ? '' : displayNameOf(tokenData);
</script>

{#if email.length > 0}
	<p class="text-sm text-default flex flex-wrap items-baseline gap-x-1.5">
		<i class="fa-solid fa-circle-user opacity-70" />
		<span>{label}</span>
		<strong class="text-gray-700 dark:text-gray-300 break-all">{email}</strong>
		{#if name.length > 0}
			<span class="text-xs">({name})</span>
		{/if}
	</p>
{/if}
